import { createSlice } from '@reduxjs/toolkit';
import {
  editOnClick,
  onViewChange,
  clearPreviewGridItems,
  manageViewSetSkipIndex,
  updateFetchShouldCall,
} from '../actions/messageBarActions';
import { viewApi } from '../service/viewApiSlice';
import { cloneDeep } from 'lodash-es';
import { IManageViewActions } from 'src/Common/dashboardSummary/store/typings';
import { artemisViewsApi } from 'src/features/HubOverview/store/service/artemisViewsApi';
import { getViewsDataApi } from 'src/features/HubOverview/store/service/multitenantDashboardApi';

export interface IPreviewDataState {
  preViewDataList: IManageViewActions;
  previewDataListArtemis: any[] | null;
  isEditable: boolean;
  manageViewSkipIndex: number;
  fetchMethodShouldCall: boolean; // This will be used to check if the fetch api should be called or not for initial load & some other cases
  manageViewActionCount: number | null;
}

const previewListInitalState: IManageViewActions = {
  actions: null,
  actionsCount: 0,
  queryId: '',
};

const initialState = {
  preViewDataList: { ...previewListInitalState },
  previewDataListArtemis: [],
  isEditable: true,
  manageViewSkipIndex: 1,
  fetchMethodShouldCall: false,
  manageViewActionCount: null,
} as IPreviewDataState;

const previewDataSlice = createSlice({
  name: 'previewDataView',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(editOnClick, (state, action) => {
      state.isEditable = action.payload;
    });
    builder.addCase(onViewChange, (state, action) => {
      if (action.payload === true) {
        state.preViewDataList = { ...previewListInitalState };
        state.previewDataListArtemis = null;
        state.manageViewActionCount = null;
        state.manageViewSkipIndex = 1;
        state.fetchMethodShouldCall = false;
      }
    });
    builder.addCase(manageViewSetSkipIndex, (state, action) => {
      state.manageViewSkipIndex = action.payload;
    });
    builder.addCase(updateFetchShouldCall, (state, action) => {
      state.fetchMethodShouldCall = action.payload;
    });
    builder.addCase(clearPreviewGridItems, (state) => {
      state.preViewDataList = { ...previewListInitalState }; // This will reset the manage action preview grid items and count
      state.previewDataListArtemis = []; // This will reset the manage action preview grid items and count
      state.manageViewActionCount = null;
    });
    builder.addMatcher(viewApi.endpoints.getPreviewDataGrid.matchFulfilled, (state, action) => {
      const currentActions = state?.preViewDataList?.actions ?? [];
      const newActions = action?.payload?.actions ?? [];

      state.preViewDataList = {
        ...state.preViewDataList,
        actions: cloneDeep([...currentActions, ...newActions]), // This will append the new data to the existing data
      };

      if (action?.payload?.actionsCount !== null && action.payload.actionsCount >= 0) {
        state.manageViewActionCount = action.payload.actionsCount;
      }
    });
    builder.addMatcher(viewApi.endpoints.getPreviewDataGrid.matchRejected, (state) => {
      state.preViewDataList = { ...previewListInitalState }; // This will reset the manage action preview grid items and count
    });
    builder.addMatcher(
      getViewsDataApi.endpoints.getTenantActionData.matchFulfilled,
      (state, action) => {
        state.previewDataListArtemis = action?.payload?.value ?? [];
        if (action?.payload?.['@odata.count']) {
          state.manageViewActionCount = action?.payload?.['@odata.count'] ?? 0;
        }
      }
    );
  },
});

export default previewDataSlice.reducer;
